/**
 * TrackerOrderRow
 *
 * Rendered inside an expanded batch. Shows order details with
 * garment count and status summary. Clicking expands to show
 * the garment items table.
 *
 * Features:
 * - Clickable order name linking to Shopify admin
 * - 3-dot menu with "View in Shopify" and "Mark Shipped" actions
 * - BLU-1509: "Switch order to DTG / DTF" for the floor owner
 */

import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronRight, MoreHorizontal, ExternalLink, PackageCheck, Sliders, Receipt, FileWarning, ArrowLeftRight } from 'lucide-react';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import { TableCell, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { TrackerGarmentTable } from './TrackerGarmentTable';
import { PrintFileIssueCallout } from './ExpandedOrderContent';
import { getShopifyAdminOrderUrl } from '@/lib/shopify-urls';
import { cn } from '@/lib/utils';
import { formatOrderNumber, type PortlessFulfilmentType } from '@threadheads/shared';
import { hybridBadge } from '@/lib/portless-fulfilment-display';
import { ClientOrgChip } from '@/components/ClientOrgChip';
import type { TrackerClientOrg } from '../hooks/useTrackerClientOrg';
import type { PrintTechnology } from '@/features/production-orders/utils/print-tech-routing';
import { orderSwitchDirections } from '../lib/print-technology-switch';
import { SwitchOrderPrintTechnologyDialog } from './SwitchOrderPrintTechnologyDialog';

interface Garment {
  id: string;
  sku: string;
  productName: string;
  size: string | null;
  color: string | null;
  status: string;
  productionMethod: string;
  /** BLU-1412 — see the identically-named field on TrackerGarmentTable's Garment. */
  printTechnology?: PrintTechnology;
  /** BLU-1412 — see the identically-named field on TrackerGarmentTable's Garment. */
  batchId?: string | null;
  isRedo: boolean;
  redoReason: string | null;
  lastActionAt: string | null;
  /** BLU-817 — see the identically-named field on TrackerGarmentTable's Garment. */
  hasOpenPrintFileIssue?: boolean;
  printFileIssueReason?: string | null;
}

interface TrackerOrderRowProps {
  order: {
    id: string;
    orderName: string;
    customerName: string | null;
    shopifyOrderId: number | null;
    productionStatus: string;
    garments: Garment[];
    locationId?: string;
    /**
     * BLU-367: the order's OWN org. A single owner-org batch can legitimately
     * carry orders from several client orgs at a shared 3PL location, so this
     * is per-ORDER, not per-batch. Optional so a stale cached payload renders
     * no chip rather than a wrong attribution.
     */
    organizationId?: string | null;
    /**
     * Hybrid vs Cross Dock, computed server-side by the shared rule. Optional so a
     * stale cached payload renders no badge rather than a wrong one.
     */
    fulfilmentType?: PortlessFulfilmentType;
    /** BLU-817: true if ANY garment on this order carries an open print-file issue. */
    hasOpenPrintFileIssue?: boolean;
  };
  /** Cross-org chip resolver, owned by the parent so it subscribes once. */
  clientOrg: TrackerClientOrg;
  /** The parent's `useCanManageFloor()`, passed to TrackerGarmentTable. */
  canManageFloor?: boolean;
  /**
   * BLU-1509: the floor owner's `dtfEnabled`, read ONCE by the parent (like
   * `clientOrg`), so "Switch order to DTF" is only offered when DTF is on.
   */
  dtfEnabled?: boolean;
  /** The batch this row is listed under: refreshed after an order switch. */
  batchId?: string;
  shopifyDomain: string | null;
  onMarkShipped?: (orderId: string, orderName: string) => void;
  hasOverrideAccess?: boolean;
  onOverrideStatus?: (target: { orderId: string; orderName: string; currentStatus: string; locationId: string }) => void;
  /** Whether invoice printing is enabled (BLU-328) */
  invoiceEnabled?: boolean;
  /** Callback to print invoice for this order (BLU-328) */
  onPrintOrderInvoice?: (orderId: string) => void;
}

/** Group garments by status and return a summary string */
function getStatusSummary(garments: Garment[]): Array<{ status: string; count: number }> {
  const counts = new Map<string, number>();
  for (const g of garments) {
    counts.set(g.status, (counts.get(g.status) ?? 0) + 1);
  }
  return Array.from(counts.entries())
    .map(([status, count]) => ({ status, count }))
    .sort((a, b) => b.count - a.count);
}

function formatStatusLabel(status: string): string {
  return status.replace(/_/g, ' ').toLowerCase().replace(/^\w/, (c) => c.toUpperCase());
}

const summaryColors: Record<string, string> = {
  PENDING: 'text-slate-600 dark:text-slate-400',
  LABEL_PRINTED: 'text-blue-600 dark:text-blue-400',
  PICKED: 'text-yellow-700 dark:text-yellow-400',
  // Phase 50.01.5 — DTF garment-status summary tones, unified across surfaces:
  // film-printed = sky, pressed = fuchsia (matches TrackerGarmentTable /
  // StatusOverrideDialog / BinManagePage). EXTERNALLY_FULFILLED re-toned to
  // slate (matching TrackerOrderTable) so it no longer collides with sky.
  FILM_PRINTED: 'text-sky-600 dark:text-sky-400',
  PRESSED: 'text-fuchsia-600 dark:text-fuchsia-400',
  CAUGHT: 'text-purple-600 dark:text-purple-400',
  IN_BIN: 'text-orange-600 dark:text-orange-400',
  PACKED: 'text-teal-600 dark:text-teal-400',
  SHIPPED: 'text-green-600 dark:text-green-400',
  FAILED: 'text-red-600 dark:text-red-400',
  // SUPERSEDED (2026-07-08): re-batch-replaced sticker — muted slate, "archived",
  // distinct from the red FAILED. Mirrors TrackerGarmentTable.
  SUPERSEDED: 'text-slate-500 dark:text-slate-500',
  EXTERNALLY_FULFILLED: 'text-slate-600 dark:text-slate-400',
};

const SHIPPABLE_STATUSES = new Set(['PACKED', 'READY_TO_PACK']);

export function TrackerOrderRow({ order, clientOrg, canManageFloor = false, dtfEnabled = false, batchId, shopifyDomain, onMarkShipped, hasOverrideAccess, onOverrideStatus, invoiceEnabled, onPrintOrderInvoice }: TrackerOrderRowProps) {
  const { t } = useTranslation('production');
  const [expanded, setExpanded] = useState(false);
  // BLU-1509: the technology an order switch is confirming, or null (closed).
  const [orderSwitchTo, setOrderSwitchTo] = useState<PrintTechnology | null>(null);
  const shouldReduceMotion = useReducedMotion();
  const statusSummary = getStatusSummary(order.garments);
  // Count excludes DISCARDED (redo discards) and SUPERSEDED (re-batch-replaced
  // stickers) garments — both are dead predecessors whose live unit is counted
  // instead. Coordinators still see them in the expanded view.
  const activeGarmentCount = order.garments.filter(g => g.status !== 'DISCARDED' && g.status !== 'SUPERSEDED').length;

  const hybrid = order.fulfilmentType ? hybridBadge(order.fulfilmentType) : null;
  // BLU-817: the flag is the authority (server-matched on order_item_id, not
  // SKU — see TrackerGarmentTable's Garment doc); the filtered list here is
  // only for the callout's count, never re-derives the flag itself.
  const hasPrintFileIssue = order.hasOpenPrintFileIssue ?? false;
  const printFileIssueGarments = order.garments.filter((g) => g.hasOpenPrintFileIssue);

  const shopifyUrl = getShopifyAdminOrderUrl(shopifyDomain, order.shopifyOrderId);
  const canMarkShipped = SHIPPABLE_STATUSES.has(order.productionStatus);
  // BLU-1509: the floor owner's "Switch order to ..." items, one per
  // direction any loaded garment can still go (the garment menu's own
  // predicate). Empty = no item, so the menu never opens empty.
  const orderSwitchTargets = useMemo(
    () => (canManageFloor && order.locationId ? orderSwitchDirections(order.garments, dtfEnabled) : []),
    [canManageFloor, order.locationId, order.garments, dtfEnabled],
  );
  const showInvoice = !!(invoiceEnabled && onPrintOrderInvoice);
  const showMarkShipped = canMarkShipped && !!onMarkShipped;
  const showOverride = !!hasOverrideAccess && !!onOverrideStatus && !!order.locationId;
  const showOrderSwitch = orderSwitchTargets.length > 0;
  const hasItemsBeforeSwitch = !!shopifyUrl || showInvoice || showMarkShipped || showOverride;
  const hasMenuItems = hasItemsBeforeSwitch || showOrderSwitch;

  return (
    <>
      <TableRow
        className="cursor-pointer hover:bg-muted/30 transition-colors group"
        onClick={() => setExpanded(!expanded)}
        tabIndex={0}
        aria-label={`Order ${order.orderName}, ${activeGarmentCount} items${hasPrintFileIssue ? `, ${t('tracker.printFileIssue.ariaBadge')}` : ''}`}
        aria-expanded={expanded}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setExpanded(!expanded);
          }
        }}
      >
        <TableCell className="py-2">
          <div className="flex flex-wrap items-center gap-2">
            <ChevronRight
              className={cn(
                'h-3.5 w-3.5 text-muted-foreground transition-transform',
                expanded && 'rotate-90'
              )}
            />
            {shopifyUrl ? (
              <a
                href={shopifyUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="font-medium text-sm text-primary hover:underline"
                onClick={(e) => e.stopPropagation()}
              >
                {formatOrderNumber(order.orderName)}
              </a>
            ) : (
              <span className="font-medium text-sm">{formatOrderNumber(order.orderName)}</span>
            )}
            {/* Hybrid only — Cross Dock is the default and gets no badge. A Hybrid
                parcel is deliberately short here (Portless adds its own half), so
                the garment count on this row is not the whole order.

                Glyph + label, no title attribute (PRODUCTION_DESIGN.md §10 — hover
                does not exist on a tablet station). The collapsed row is too dense
                for the sentence; expanding the row surfaces it as a visible callout
                above the garment table, mirroring the packing slip. */}
            {hybrid && (
              <Badge variant={hybrid.variant} size="sm" className="shrink-0 whitespace-nowrap">
                <hybrid.icon className="h-3 w-3" aria-hidden="true" />
                {t(hybrid.labelKey)}
              </Badge>
            )}
            {/* BLU-367: which client this order belongs to. A cross-org batch
                mixes clients, so this is the only place the operator can tell
                them apart. Own-org rows render nothing. */}
            {clientOrg.isCrossOrg(order.organizationId) && (
              <ClientOrgChip label={clientOrg.labelFor(order.organizationId)} />
            )}
            {/* BLU-817: destructive (red), not the Hybrid/Split blue/amber
                tones already on this row — an open print-file issue is a
                hard block (PRODUCTION_DESIGN.md §4.1: the garment cannot be
                filmed until it's fixed), not an in-progress state. The
                reason lives in the expanded callout below, never a title
                attribute (§10). */}
            {hasPrintFileIssue && (
              <Badge variant="destructive" size="sm" className="shrink-0 whitespace-nowrap gap-1">
                <FileWarning className="h-3 w-3" aria-hidden="true" />
                {t('tracker.printFileIssue.orderBadge')}
              </Badge>
            )}
          </div>
        </TableCell>
        <TableCell className="py-2 text-sm text-muted-foreground">
          {order.customerName ?? '-'}
        </TableCell>
        <TableCell className="py-2 text-sm tabular-nums text-right">
          {activeGarmentCount}
        </TableCell>
        <TableCell className="py-2">
          <div className="flex items-center gap-1.5">
            <div className="flex flex-wrap gap-1.5 flex-1">
              {statusSummary.map(({ status, count }) => (
                <span
                  key={status}
                  className={cn(
                    'text-xs tabular-nums',
                    summaryColors[status] ?? 'text-muted-foreground'
                  )}
                >
                  {count} {formatStatusLabel(status)}
                </span>
              ))}
            </div>
            {hasMenuItems && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                  <Button
                    variant="ghost"
                    size="icon"
                    className={cn(
                      'h-6 w-6 transition-opacity shrink-0',
                      canMarkShipped
                        ? 'opacity-60 hover:opacity-100'
                        : 'opacity-0 group-hover:opacity-100'
                    )}
                  >
                    <MoreHorizontal className="h-3.5 w-3.5" />
                    <span className="sr-only">{t('tracker.orderRow.orderActions')}</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  {shopifyUrl && (
                    <DropdownMenuItem
                      onClick={(e) => {
                        e.stopPropagation();
                        window.open(shopifyUrl, '_blank', 'noopener,noreferrer');
                      }}
                    >
                      <ExternalLink className="h-4 w-4 mr-2" />
                      {t('tracker.orderRow.viewInShopify')}
                    </DropdownMenuItem>
                  )}
                  {invoiceEnabled && onPrintOrderInvoice && (
                    <>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        onClick={(e) => {
                          e.stopPropagation();
                          onPrintOrderInvoice(order.id);
                        }}
                      >
                        <Receipt className="h-4 w-4 mr-2" />
                        {t('tracker.invoice.printInvoice')}
                      </DropdownMenuItem>
                    </>
                  )}
                  {canMarkShipped && onMarkShipped && <DropdownMenuSeparator />}
                  {canMarkShipped && onMarkShipped && (
                    <DropdownMenuItem
                      onClick={(e) => {
                        e.stopPropagation();
                        onMarkShipped(order.id, order.orderName);
                      }}
                    >
                      <PackageCheck className="h-4 w-4 mr-2" />
                      {t('tracker.orderRow.markShipped')}
                    </DropdownMenuItem>
                  )}
                  {hasOverrideAccess && onOverrideStatus && order.locationId && (
                    <>
                      {(!!shopifyUrl || canMarkShipped) && <DropdownMenuSeparator />}
                      <DropdownMenuItem
                        onClick={(e) => {
                          e.stopPropagation();
                          onOverrideStatus({
                            orderId: order.id,
                            orderName: order.orderName,
                            currentStatus: order.productionStatus,
                            locationId: order.locationId!,
                          });
                        }}
                      >
                        <Sliders className="h-4 w-4 mr-2" />
                        {t('tracker.orderRow.overrideStatus')}
                      </DropdownMenuItem>
                    </>
                  )}
                  {showOrderSwitch && (
                    <>
                      {hasItemsBeforeSwitch && <DropdownMenuSeparator />}
                      {orderSwitchTargets.map((to) => (
                        <DropdownMenuItem
                          key={to}
                          onClick={(e) => {
                            e.stopPropagation();
                            setOrderSwitchTo(to);
                          }}
                        >
                          <ArrowLeftRight className="h-4 w-4 mr-2" />
                          {t('tracker.printTechSwitch.order.menuItem', { tech: to })}
                        </DropdownMenuItem>
                      ))}
                    </>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
        </TableCell>
      </TableRow>

      <AnimatePresence>
        {expanded && (
          <tr>
            <td colSpan={10} className="p-0">
              <motion.div
                initial={shouldReduceMotion ? {} : { opacity: 0, height: 0 }}
                animate={shouldReduceMotion ? {} : { opacity: 1, height: 'auto' }}
                exit={shouldReduceMotion ? {} : { opacity: 0, height: 0 }}
                transition={{ duration: 0.2 }}
                className="overflow-hidden"
              >
                <div className="pl-12 pr-4 py-1 bg-muted/20">
                  {/* BLU-817: renders FIRST — an actionable problem, not
                      routing info like the Hybrid callout below it. */}
                  {hasPrintFileIssue && (
                    <PrintFileIssueCallout count={printFileIssueGarments.length} />
                  )}
                  {/* The sentence the collapsed row's pill cannot carry. Placed
                      ABOVE the garment table because it is the reason that table is
                      short: Portless adds the rest of the parcel from its own stock,
                      and without this an operator reads the shortfall as a picking
                      error.

                      A visible callout rather than the pill's `title`:
                      PRODUCTION_DESIGN.md §10 bans tooltips carrying critical info
                      (tablets, no hover) and §4.2 floors operator-critical type
                      above text-xs. Same blue, same PackageOpen glyph and the same
                      wording as PackingSlip's callout — one concept, one look,
                      wherever the operator meets it. */}
                  {hybrid && (
                    <div className="my-2 flex items-start gap-3 rounded-lg border border-blue-200 bg-blue-50 p-3 dark:border-blue-800 dark:bg-blue-950/30">
                      <hybrid.icon className="mt-0.5 h-5 w-5 flex-shrink-0 text-blue-600" aria-hidden="true" />
                      <div>
                        <p className="text-base font-bold text-blue-800 dark:text-blue-200">
                          {t(hybrid.labelKey)}
                        </p>
                        <p className="text-sm text-blue-700 dark:text-blue-300">
                          {t(hybrid.descriptionKey)}
                        </p>
                      </div>
                    </div>
                  )}
                  <TrackerGarmentTable garments={order.garments} orderId={order.id} canManageFloor={canManageFloor} />
                </div>
              </motion.div>
            </td>
          </tr>
        )}
      </AnimatePresence>

      {/* A sibling of the rows, not a child: React bubbles events out of a
          portal, so a click (or a space typed in the note) inside the dialog
          must never reach the row and toggle it. */}
      {orderSwitchTo && order.locationId && (
        <SwitchOrderPrintTechnologyDialog
          open={!!orderSwitchTo}
          onOpenChange={(open) => { if (!open) setOrderSwitchTo(null); }}
          target={{
            orderId: order.id,
            orderName: order.orderName,
            to: orderSwitchTo,
            locationId: order.locationId,
            batchId,
            garments: order.garments,
          }}
        />
      )}
    </>
  );
}
